import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Descriptions, Row, Segmented, Space, Spin, Statistic, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { useTranslation } from '../../../../shared/i18n';
import { useChartTokens } from '../../dashboard/charts';
import tmService from '../../../services/tmService';
import { Copyable, UsageBar, fmtNum, toastErr } from './common';
import { LevelTag } from './TrustCard';

const { Text, Paragraph } = Typography;

// public API host (nginx maps api.tm.yeapcloud.net/v1/* → /api/tm/v1/*)
export const API_BASE = 'https://api.tm.yeapcloud.net/v1';
const API_URL = `${API_BASE}/send`;

const SERIES = [
    { key: 'sent', name: '发送', color: '#2357ff' },
    { key: 'delivered', name: '送达', color: '#16a34a' },
    { key: 'bounced', name: '退信', color: '#dc2626' },
    { key: 'complained', name: '投诉', color: '#c026d3' },
    { key: 'opened', name: '打开', color: '#0891b2' },
    { key: 'clicked', name: '点击', color: '#d97706' },
];

const pct = (v) => `${((v || 0) * 100).toFixed(2)}%`;

const Overview = ({ stream }) => {
    const { t } = useTranslation();
    const tok = useChartTokens();
    const [days, setDays] = useState(7);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        tmService.getStats(stream.id, days)
            .then((r) => alive && setStats(r?.data || null))
            .catch((e) => toastErr(e, t('Failed to load')))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [stream.id, days, t]);

    // 后端只返回有数据的日期，这里补齐空日
    const rows = useMemo(() => {
        const map = {};
        (stats?.daily || []).forEach((d) => { map[d.date] = d; });
        const out = [];
        for (let i = days - 1; i >= 0; i--) {
            const date = dayjs().subtract(i, 'day').format('YYYY-MM-DD');
            const d = map[date] || {};
            out.push({
                date,
                sent: (d.total || 0) - (d.rejected || 0),
                delivered: d.delivered || 0, bounced: d.bounced || 0, complained: d.complained || 0,
                opened: d.opened || 0, clicked: d.clicked || 0,
            });
        }
        return out;
    }, [stats, days]);

    const option = useMemo(() => ({
        animation: false,
        color: SERIES.map((s) => s.color),
        grid: { left: 40, right: 12, top: 36, bottom: 24 },
        legend: { top: 0, textStyle: { color: tok.text2 }, itemWidth: 12, itemHeight: 8 },
        tooltip: { trigger: 'axis', backgroundColor: tok.tipBg, borderColor: tok.line, textStyle: { color: tok.tipText, fontSize: 12 } },
        xAxis: {
            type: 'category', data: rows.map((r) => r.date.slice(5)),
            axisLine: { lineStyle: { color: tok.line } }, axisTick: { show: false }, axisLabel: { color: tok.text3, fontSize: 11 },
        },
        yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: tok.line } }, axisLabel: { color: tok.text3, fontSize: 11 } },
        series: SERIES.map((s) => ({
            type: 'line', name: t(s.name), data: rows.map((r) => r[s.key]), showSymbol: false, smooth: 0.2, lineStyle: { width: 2 },
        })),
    }), [rows, tok, t]);

    const tot = stats?.totals || {};
    const rates = stats?.rates || {};
    const host = stream.smtp_host || stream.smtp?.host;

    const curl = `curl -X POST ${API_URL} \\
  -H "Authorization: Bearer ytm_xxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "from": "noreply@your-domain.com",
    "to": "user@example.com",
    "subject": "Hello",
    "html": "<p>Hello</p>",
    "text": "Hello",
    "category": "welcome"
  }'`;

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                    <Card size="small" title={t('Usage')}>
                        <UsageBar usage={stream.usage} />
                        <div style={{ marginTop: 8, fontSize: 12 }}>
                            <Text type="secondary">{t('Plan')}：</Text>{stream.plan ? t(stream.plan.name) : '-'}
                            {stream.expiry_date ? <span> · <Text type="secondary">{t('Expires')}：</Text>{String(stream.expiry_date).slice(0, 10)}</span> : null}
                        </div>
                        <div style={{ fontSize: 12 }}>
                            <Text type="secondary">{t('Domains')}：</Text>{stream.domains_used ?? '-'} / {stream.limits?.domains === -1 ? t('Unlimited') : stream.limits?.domains}
                            {stream.sandbox ? <span> · <Text type="secondary">{t('Sandbox daily')}：</Text>{stream.limits?.sandbox_daily}</span> : null}
                        </div>
                        {stream.trust && (
                            <div style={{ fontSize: 12, marginTop: 4 }}>
                                <Text type="secondary">{t('Trust level')}：</Text><LevelTag level={stream.trust.level} />
                                {!stream.trust.free_plan && (
                                    <Tooltip title={t('Your level sets the sending rate on paid plans: hourly cap = plan cap × level factor')}>
                                        <Text type="secondary"> · {t('Sending rate')} ×{stream.trust.hourly_factor}</Text>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={16}>
                    <Card size="small" title={t('Last {{n}} days', { n: days })}
                        extra={<Segmented size="small" value={days} onChange={setDays} options={[{ value: 7, label: t('7 days') }, { value: 30, label: t('30 days') }]} />}>
                        <Row gutter={8}>
                            <Col span={6}><Statistic title={t('Send')} value={(tot.total || 0) - (tot.rejected || 0)} valueStyle={{ fontSize: 18 }} /></Col>
                            <Col span={6}><Statistic title={t('Delivery rate')} value={pct(rates.delivered)} valueStyle={{ fontSize: 18 }} /></Col>
                            <Col span={6}><Statistic title={t('Bounce rate')} value={pct(rates.bounced)} valueStyle={{ fontSize: 18 }} /></Col>
                            <Col span={6}><Statistic title={t('Complaint rate')} value={pct(rates.complained)} valueStyle={{ fontSize: 18 }} /></Col>
                        </Row>
                    </Card>
                </Col>
            </Row>
            <Card size="small">
                <Spin spinning={loading}>
                    <ReactECharts option={option} style={{ height: 260 }} notMerge />
                </Spin>
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('Opens {{o}} · Clicks {{c}}', { o: fmtNum(tot.opened), c: fmtNum(tot.clicked) })}
                </Text>
            </Card>
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={10}>
                    <Card size="small" title="SMTP">
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label={t('Host')}><Copyable value={host} /></Descriptions.Item>
                            <Descriptions.Item label={t('Port')}>
                                <Space size={4} wrap><Text code>587</Text><Text type="secondary">STARTTLS</Text><Text code>465</Text><Text type="secondary">TLS</Text></Space>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Username')}><Copyable value="apikey" /></Descriptions.Item>
                            <Descriptions.Item label={t('Password')}>
                                <Space size={4}>{t('API key')}
                                    <Tooltip title={t('Create one on the API Keys page')}><QuestionCircleOutlined /></Tooltip>
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Category header')}>
                                <Text code>{stream.smtp?.category_header || 'X-Yeap-Category'}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </Col>
                <Col xs={24} lg={14}>
                    <Card size="small" title="HTTP API">
                        <Copyable value={`POST ${API_URL}`} />
                        <Paragraph copyable={{ text: curl }} style={{ marginTop: 8, marginBottom: 0 }}>
                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{curl}</pre>
                        </Paragraph>
                    </Card>
                </Col>
            </Row>
        </Space>
    );
};

export default Overview;
